import { useCallback, useState } from 'react';
import { ConfirmCandidate as LegacyConfirmCandidate } from '~/components/ConfirmCandidate/ConfirmCandidate';
import { useSelector } from 'react-redux';
import { generatePath, useLocation, useNavigate, useParams } from 'react-router';
import { AdditionalDetails, LargeShiftDetails, Receipt } from '@tempmee/react-blocks';
import { ConfirmExitPopup } from '~/components/ConfirmCandidate/components/ConfirmExitPopup';
import { ConfirmOrDecline } from '~/components/ConfirmCandidate/components/ConfirmOrDecline';
import { useConfirmExitNavigation } from '~/components/ConfirmCandidate/hooks/useConfirmExitNavigation';
import { useConfirmOutreachSource } from '~/components/ConfirmCandidate/hooks/useConfirmOutreachSource';
import { useCheckPaymentExpiration } from '~/domain/Payments';
import { useMediaQuery } from '~/hooks';
import { fireCandidateListAdditionalDetailsClicked } from '~/posthogs/events';
import { COLLAPSE_BREAKPOINT, DetailsPageShell } from '~/components/shared/DetailsPage';
import { routes } from '~/routes';
import { useHoldShiftDetailsPage } from '../shiftDetailsPageHold';
import type { IShiftDetailsPageProps } from '../shiftDetailsPages';
import { toAdditionalDetailsRows, toReceipt } from '../shared/utils/shiftPanelsProps';
import {
  earliestOfferExpiry,
  formatOfferExpiry,
  toShiftSummaryFields,
} from '../shared/utils/shiftSummaryProps';
import { ConfirmCandidatesPrimary } from './components/ConfirmCandidatesPrimary';
import { ConfirmOutcomePopups } from './components/ConfirmOutcomePopups';
import { CounterAdjustmentPanel } from './components/CounterAdjustmentPanel';
import { ShiftAlertSlot } from './components/ShiftAlertSlot';
import { useCandidateImpression } from './hooks/useCandidateImpression';
import { useConfirmCandidateActions } from './hooks/useConfirmCandidateActions';
import { useConfirmCandidatesData } from './hooks/useConfirmCandidatesData';
import { useShiftActionsMenu } from './hooks/useShiftActionsMenu';
import { hasCounterOffer } from '../shared/utils/candidateCardProps';

/**
 * CONFIRM CANDIDATES — the redesigned page for a shift whose professionals have
 * applied, and the single-candidate page behind it.
 *
 * One component owns both because they are one job to be done: picking who works
 * the shift. Which one renders is a question of how much there is to pick from,
 * which {@link useCandidateSelection} answers.
 *
 * This file is composition only. The behaviour lives in four hooks — selection,
 * the impression events, the confirm/decline follow-ups and the overflow menu —
 * so each can be read and tested as one idea rather than as a slice of a page.
 *
 * The legacy `ConfirmCandidate` screen is untouched: with the flag off the route
 * still renders it, and its confirm/decline component and popups are reused here
 * by import rather than reimplemented.
 */
const ConfirmCandidatesRedesign = ({ job: routeJob }: IShiftDetailsPageProps) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const params = useParams<{ jobId?: string; candidateId?: string }>();
  const jobId = routeJob?.id ?? params.jobId;

  const officeUser = useSelector(
    (state: {
      user: {
        user?: { notes_for_temp?: string };
        selectedChildOffice?: { notes_for_temp?: string };
      };
    }) => state.user.selectedChildOffice ?? state.user.user,
  );

  const [isCollapsed] = useMediaQuery([COLLAPSE_BREAKPOINT]);
  const [notesForTemp, setNotesForTemp] = useState(officeUser?.notes_for_temp ?? '');

  const {
    job,
    authorizations,
    isLoading,
    hasLoadedCandidates,
    counters,
    isSingleCandidate,
    activeCounter,
    openCandidate,
    clearSelection,
  } = useConfirmCandidatesData({ shiftId: jobId, routeCandidateId: params.candidateId });

  // Attributes a confirmation-outreach email landing (GOTU-4906) exactly as the
  // legacy screen does — this link lands on the page rather than navigating to
  // it, so there is no earlier click to hang the attribution on.
  useConfirmOutreachSource({ jobId: job?.id, jobStatus: job?.status });

  const { isInvalidPayment } = useCheckPaymentExpiration(job?.offer_owner?.id);

  const {
    allowNavigation,
    clearPendingNavigation,
    navigateToPending,
    handleNavigateWithoutBlocking,
    confirmationExitModalOpen,
    setConfirmationExitModalOpen,
  } = useConfirmExitNavigation({
    jobId,
    isError: false,
    hasCandidatesAvailable: counters.length > 0,
  });

  /**
   * The header's back arrow. On a candidate picked from the list it returns to
   * the list — the selection is page state, not a route, so a history back
   * would leave the shift instead. Everywhere else (the list itself, or a sole
   * applicant with no list behind them) it leaves for the shift list exactly as
   * the legacy screen's back button did — through the exit guard, so an office
   * leaving with candidates still waiting gets the "Candidate Not Confirmed"
   * popup, as it did there.
   *
   * Left in the page rather than pushed into a hook: it is the one decision that
   * genuinely needs the selection, the navigation guard and the router at once.
   */
  const goBack = useCallback(() => {
    if (activeCounter && !isSingleCandidate) {
      clearSelection();
      return;
    }

    navigate(generatePath(routes.shiftsOverview, { type: 'pending' }));
  }, [activeCounter, clearSelection, isSingleCandidate, navigate]);

  const { confirmOrDeclineProps, outcome, isMutating, hasBlockingPrompt, hasOpenEpilogue } =
    useConfirmCandidateActions({ job, counter: activeCounter, allowNavigation });

  // A confirm flips the shift to `filled` and a decline of the last candidate
  // reopens it, and the gate picks the page from that status — so without this
  // the page unmounts the moment the refetch lands and takes its own popup with
  // it, before the office can answer "invite them for other shifts?". Held only
  // while a popup is up; every one of them navigates away when dismissed.
  useHoldShiftDetailsPage(hasOpenEpilogue);

  const {
    items: menuItems,
    invitePopup,
    cancelPopup,
    isCancelPending,
  } = useShiftActionsMenu({ shiftId: jobId, job });

  const isBusy = isLoading || isMutating || isCancelPending || hasBlockingPrompt;

  // The same conditions `ConfirmCandidatesPrimary` checks before it renders the list.
  const isListShowing = !isBusy && counters.length > 0 && activeCounter === null;

  const { viewedPosition, reportCandidateClicked, reportLeaveClicked } = useCandidateImpression({
    shiftId: jobId,
    counters,
    activeCounter,
    isListShowing,
    pathname,
  });

  const selectCandidate = useCallback(
    (candidateId: string) => {
      reportCandidateClicked(candidateId);
      openCandidate(candidateId);
    },
    [openCandidate, reportCandidateClicked],
  );

  const receipt = toReceipt({ job, counter: activeCounter });

  const alert = (
    <ShiftAlertSlot
      isPaymentInvalid={Boolean(isInvalidPayment)}
      hasCounterOffer={Boolean(activeCounter) && hasCounterOffer(activeCounter, job)}
      competingOffersCount={activeCounter?.competingOffersCount}
      counterOfferAlert={<CounterAdjustmentPanel job={job} counter={activeCounter} />}
    />
  );

  return (
    <>
      <DetailsPageShell
        testIDPrefix="shift-details"
        actionsLabel="Shift actions"
        title={isSingleCandidate || activeCounter ? 'Confirm Candidate' : 'Confirm Candidates'}
        actions={menuItems}
        onBack={goBack}
        summary={
          <LargeShiftDetails
            status="confirm-candidates"
            {...toShiftSummaryFields(job)}
            expiryNote={formatOfferExpiry(
              activeCounter?.expiration ?? earliestOfferExpiry(counters),
              job?.timezone,
            )}
            data-testid="shift-summary"
          />
        }
        primary={
          <ConfirmCandidatesPrimary
            job={job}
            counters={counters}
            activeCounter={activeCounter}
            authorizations={authorizations}
            isBusy={isBusy}
            hasLoadedCandidates={hasLoadedCandidates}
            isCollapsed={isCollapsed}
            alert={alert}
            onSelectCandidate={selectCandidate}
            actions={
              <ConfirmOrDecline
                {...confirmOrDeclineProps}
                job={job}
                allCandidates={counters}
                viewedPosition={viewedPosition}
                notesForTemp={notesForTemp}
              />
            }
          />
        }
        sidebar={
          <>
            <AdditionalDetails
              variant="open-confirm"
              {...toAdditionalDetailsRows(job)}
              notes={notesForTemp}
              onNotesChange={setNotesForTemp}
              defaultOpen={false}
              onOpenChange={(isOpen) =>
                fireCandidateListAdditionalDetailsClicked({
                  action: isOpen ? 'expand' : 'collapse',
                })
              }
              data-testid="additional-details"
            />
            <Receipt
              lines={receipt.lines}
              total={receipt.total}
              defaultOpen={false}
              data-testid="receipt"
            />
          </>
        }
      />

      <ConfirmOutcomePopups
        job={job}
        {...outcome}
        allowNavigation={allowNavigation}
        navigateWithoutBlocking={handleNavigateWithoutBlocking}
      />

      {confirmationExitModalOpen ? (
        <ConfirmExitPopup
          onGoBack={() => {
            clearPendingNavigation();
            setConfirmationExitModalOpen(false);
          }}
          onLeave={() => {
            reportLeaveClicked();
            navigateToPending();
          }}
        />
      ) : null}

      {invitePopup}
      {cancelPopup}
    </>
  );
};

/**
 * CONFIRM CANDIDATES, as registered in `SHIFT_DETAILS_PAGES`.
 *
 * An onsite interview with applicants is a different page: the legacy screen
 * runs the permanent-hire decision, while this one is shaped for a shift — a
 * money `Receipt`, a rate-and-hours summary, and a menu offering "Invite
 * Professionals" and "Cancel Shift", none of which a permanent hire has. Interviews
 * are out of the redesign (design, 2026-09-16), so they stay on the screen they
 * have today — the same split the other four state pages make.
 *
 * A component boundary rather than an early return, so this page's candidate,
 * credentials and overtime queries never run on the delegated path, where the
 * legacy screen runs its own.
 */
export const ConfirmCandidatesPage = ({ job }: IShiftDetailsPageProps) => {
  if (job.isOnsiteInterview) {
    return <LegacyConfirmCandidate />;
  }

  return <ConfirmCandidatesRedesign job={job} />;
};
